import React, { useState } from 'react';

/**
 * GridExportModal — lets the user choose what to export from a data grid,
 * similar to the progress tracker exports.
 *
 * Options:
 *   - 'dashboard' : only the columns currently visible in the dashboard
 *   - 'all'       : every available column
 *   - 'template'  : headers only (empty), for filling in and re-importing
 *
 * @param {boolean} isOpen
 * @param {Function} onClose
 * @param {Function} onExport - called with the chosen mode ('dashboard'|'all'|'template')
 * @param {boolean} isExporting
 * @param {string} [title='Export to Excel']
 * @param {string} [accentColor='#0d6efd']
 * @param {number} [visibleCount] - number of currently visible columns (for the label)
 * @param {number} [totalCount] - total number of columns (for the label)
 */
const GridExportModal = ({
  isOpen,
  onClose,
  onExport,
  isExporting = false,
  title = 'Export to Excel',
  accentColor = '#0d6efd',
  visibleCount,
  totalCount,
}) => {
  const [mode, setMode] = useState('dashboard');
  if (!isOpen) return null;

  const OPTIONS = [
    { id: 'dashboard', label: 'Dashboard columns only', desc: visibleCount != null ? `Export the ${visibleCount} column(s) currently shown` : 'Export only the columns currently shown' },
    { id: 'all', label: 'All columns', desc: totalCount != null ? `Export all ${totalCount} columns` : 'Export every available column' },
    { id: 'template', label: 'Empty template', desc: 'Headers only (all columns) — fill in and re-import' },
  ];

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 10001 }}>
      <div style={{ backgroundColor: 'white', borderRadius: 10, padding: '1.75rem', width: '90%', maxWidth: 460, boxShadow: '0 10px 30px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h3 style={{ margin: 0, color: '#1f2937', fontSize: '1.15rem' }}>📤 {title}</h3>
          <button onClick={onClose} disabled={isExporting} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: isExporting ? 'not-allowed' : 'pointer', color: '#6b7280' }}>×</button>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.5rem' }}>
          {OPTIONS.map((opt) => (
            <label key={opt.id} style={{
              display: 'flex', alignItems: 'flex-start', gap: '0.6rem', padding: '0.7rem 0.9rem',
              border: `1px solid ${mode === opt.id ? accentColor : '#e5e7eb'}`, borderRadius: 8, cursor: 'pointer',
              backgroundColor: mode === opt.id ? '#f5f7ff' : 'white',
            }}>
              <input type="radio" name="grid-export-mode" value={opt.id} checked={mode === opt.id} onChange={() => setMode(opt.id)} style={{ marginTop: 3, accentColor }} />
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#1f2937' }}>{opt.label}</div>
                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
          <button onClick={onClose} disabled={isExporting} style={{ padding: '0.6rem 1.2rem', backgroundColor: '#6b7280', color: 'white', border: 'none', borderRadius: 6, cursor: isExporting ? 'not-allowed' : 'pointer', fontSize: '0.9rem' }}>Cancel</button>
          <button onClick={() => onExport(mode)} disabled={isExporting} style={{ padding: '0.6rem 1.2rem', backgroundColor: accentColor, color: 'white', border: 'none', borderRadius: 6, cursor: isExporting ? 'not-allowed' : 'pointer', fontSize: '0.9rem', fontWeight: 600 }}>
            {isExporting ? 'Exporting…' : '📊 Export'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default GridExportModal;
